"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import FeedbackForm from "./feedbackForm";
export type FAQ = { question: string; answer: React.ReactNode };
export const DEFAULT_FAQS: FAQ[] = [
     {
      question: "What’s a “credit”?",
      answer:
        "A credit is how prompts are billed. Every 50 prompt-words (or any part thereof) uses 1 credit.",
    },
    {
      question: "Can I switch between Page Builder and Card Maker?",
      answer:
        "Yes. Use the Page Builder dropdown at the top or choose a service on New Chat.",
    },
    {
      question: "How do I request changes after generation?",
      answer:
        "Use Edit with Prompt (natural language) or open Editor Mode to modify the code yourself.",
    },
    {
      question: "What can I do for free vs paid?",
      answer:
        "You can generate and Preview. Download Code, Editor Mode, and Share public link are available with a one-time paid unlock.",
    },
    {
      question: "What is “Redesign Website”?",
      answer:
        "You provide your current site/needs; Varnz creates a new look and structure. This flow consumes 5 credits.",
    },
    {
      question: "Do voice prompts cost extra?",
      answer: "No—voice and text follow the same credit rule based on prompt length.",
    },
    {
      question: "What happens to my work—private or public?",
      answer: "Your projects stay private unless you choose Share public link.",
    },
    {
      question: "I ran out of credits—what next?",
      answer: "Purchase more credits and continue from where you left off.",
    },
    {
      question: "Can I edit after sharing?",
      answer:
        "Yes. Update via Prompt or Editor Mode, then share again (the link updates).",
    },
    {
      question: "Any tips for better results?",
      answer:
        "Be specific: mention sections (hero, features, pricing), tone (minimal/elegant), and constraints (colors, fonts, buttons). Short, clear prompts save credits.",
    },
];


export default function FloatingHelpSidebar({
  videoUrl = "https://www.youtube.com/embed/dQw4w9WgXcQ",
  onFeedbackSubmit,
  extraTabs = [],
  faqs = DEFAULT_FAQS,
  position = "right",
}: {
  videoUrl?: string;
  onFeedbackSubmit?: (payload: {
    name?: string;
    email?: string;
    message: string;
    rating?: number;
  }) => Promise<void> | void;
  extraTabs?: Array<{
    id: string;
    label: string;
    content: React.ReactNode;
    icon?: React.ReactNode;
  }>;
  faqs?: FAQ[];
  position?: "right" | "left";
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string>("how");
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  // Preview helper: detect iframe/canvas to show an inline open button
  const [previewMode, setPreviewMode] = useState(false);
  useEffect(() => {
    try { setPreviewMode(window.self !== window.top); } catch { setPreviewMode(false); }
  }, []);

  // Accessibility: close on ESC
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Return focus to trigger when closing
  useEffect(() => {
    if (!open) {
      triggerRef.current?.focus({ preventScroll: true });
    }
  }, [open]);

  const tabs = useMemo(() => {
    const base = [
      {
        id: "how",
        label: "How it works",
        icon: <InfoIcon className="h-4 w-4" />,
        content: <HowItWorksContent />, 
      },
      // {
      //   id: "video",
      //   label: "Video",
      //   icon: <PlayIcon className="h-4 w-4" />,
      //   content: <VideoContent url={videoUrl} />, 
      // },
      {
        id: "faqs",
        label: "FAQs & Pricing",
        icon: <QuestionIcon className="h-4 w-4" />,
        content: <FAQsContent items={faqs} />, 
      },
      {
        id: "more",
        label: "More",
        icon: <SparklesIcon className="h-4 w-4" />,
        content: <MoreContent />, 
      },
    ];

    const extras = extraTabs.map((t) => ({
      id: t.id,
      label: t.label,
      icon: t.icon ?? <DotIcon className="h-4 w-4" />,
      content: t.content,
    }));

    return [...base, ...extras];
  }, [extraTabs, onFeedbackSubmit, videoUrl]);

  const side = position === "left" ? "left-0" : "right-0";

  // Inline style fallback (works even if Tailwind isn't present)
  const fabStyle = useMemo(() => ({
    position: "fixed" as const,
    bottom: 20,
    ...(position === "left" ? { left: 20 } : { right: 20 }),
    zIndex: 50,
    borderRadius: 9999,
    padding: 16,
    background: "#111",
    color: "#fff",
    boxShadow: "0 10px 15px -3px rgba(0,0,0,.2), 0 4px 6px -2px rgba(0,0,0,.1)",
  }), [position]);

  return (
    <>
      {/* Preview helper (only inside iframes / canvas) */}
      {previewMode && (
        <div style={{ position: "absolute", top: 8, left: 8, zIndex: 60 }}>
          <button type="button" onClick={() => setOpen(true)}
            style={{ padding: "8px 12px", borderRadius: 10, border: "1px solid #e5e5e5", background: "#fff", cursor: "pointer" }}>
            Open Help
          </button>
        </div>
      )}
      {/* Floating Action Button */}
      <button
        ref={triggerRef}
        type="button"
        style={fabStyle}
        aria-label="Open help & info"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-50 rounded-full p-4 shadow-xl bg-gradient-to-tr from-neutral-900 to-neutral-700 text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black"
      >
        <HelpCircle className="h-6 w-6" />
      </button>

      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 transition ${
          open ? "visible opacity-100" : "invisible opacity-0"
        } bg-black/40 backdrop-blur-[1px]`}
        onClick={() => setOpen(false)}
      />

      {/* Drawer */}
      <aside
        ref={drawerRef}
        role="dialog"
        aria-labelledby="help-drawer-title"
        aria-modal="true"
        className={`fixed top-0 ${side} z-[60] h-dvh w-[92%] sm:w-[480px] max-w-full bg-white dark:bg-neutral-900 shadow-2xl border-l border-neutral-200 dark:border-neutral-800 transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : position === "left" ? "-translate-x-full" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between gap-2 p-4 border-b border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-900/80 backdrop-blur">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 grid place-content-center rounded-xl bg-neutral-900 text-white dark:bg-white dark:text-black">
              <HelpCircle className="h-5 w-5" />
            </div>
            <h2 id="help-drawer-title" className="text-base font-semibold">
              Help & Info
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close sidebar"
            onClick={() => setOpen(false)}
            className="rounded-lg p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-4">
          <div role="tablist" aria-label="Help tabs" className="mt-2 flex gap-1 overflow-x-auto no-scrollbar">
            {tabs.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={active === t.id}
                onClick={() => setActive(t.id)}
                className={`group flex items-center gap-2 rounded-xl px-3 py-2 text-sm transition ${
                  active === t.id
                    ? "bg-neutral-900 text-white dark:bg-white dark:text-black"
                    : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
                }`}
              >
                {t.icon}
                <span className="whitespace-nowrap">{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto h-[calc(100dvh-112px)]">
          {tabs.map((t) => (
            <section
              key={t.id}
              role="tabpanel"
              aria-hidden={active !== t.id}
              className={active === t.id ? "block" : "hidden"}
            >
              {t.content}
            </section>
          ))}
        </div>
      </aside>
    </>
  );
}

/* -------------------- Tab Contents -------------------- */

function HowItWorksContent() {
  const sections = [
    {
      title: "How It Works",
      items: [
        {
          title: "Pick a service",
          desc: [
            "Page Builder is selected by default. Switch to Card Maker anytime from the top dropdown or choose a service on the New Chat screen.",
          ],
        },
        {
          title: "Describe or speak your idea",
          desc: [
            "Type your prompt (or use the mic) and press Send. Varnz generates a web page or card from your instructions.",
          ],
        },
        {
          title: "Review & refine",
          desc: [
            "Edit with Prompt: Ask for tweaks in plain language (e.g., “make the hero darker, add CTA, increase spacing”).",
            "Editor Mode: Open the code editor to make changes yourself.",
          ],
        },
        {
          title: "Export & share",
          desc: [
            "Preview your result.",
            "Download Code, use Editor Mode, and Share a public link with a one-time paid unlock.",
            "When you’re done, share the public link anywhere.",
          ],
        },
        {
          title: "Redesign an existing site",
          desc: [
            "Paste your current site details or brief—Varnz will create a fresh look. (Redesigns use 5 credits.)",
          ],
        },
      ],
    },
    {
      title: "Services",
      items: [
        {
          title: "Create Web Page",
          desc: ["Build responsive pages from a simple prompt or voice."],
        },
        {
          title: "Redesign Website",
          desc: ["Get a different visual style/layout for an existing site."],
        },
        {
          title: "Create Cards",
          desc: [
            "Instant greeting, invitation, or business cards from text or voice.",
          ],
        },
        {
          title: "Edit by Prompt or Editor",
          desc: ["Tweak via natural language or code."],
        },
        {
          title: "Preview / Download / Share",
          desc: ["See it live, export code, or share a public link."],
        },
      ],
    },
    {
      title: "Features",
      items: [
        {
          title: "Text or Voice Prompts",
          desc: ["Build without manual design or code."],
        },
        {
          title: "Fast Draft → Polished Output",
          desc: ["Iterate quickly with prompt edits."],
        },
        {
          title: "Editor Mode",
          desc: ["Directly adjust HTML, CSS, and JS when you want control."],
        },
        {
          title: "Public Share Link",
          desc: ["Share your work instantly after unlocking."],
        },
        {
          title: "Flexible Workflow",
          desc: ["Start with Page Builder or Card Maker; switch anytime."],
        },
        {
          title: "Explore",
          desc: ["Browse example sites or cards for inspiration."],
        },
      ],
    }
  ];

  return (
    <div className="space-y-6">
      {sections.map((section, si) => (
        <div key={si}>
          <h3 className="text-lg font-semibold mb-3">{section.title}</h3>
          <ol className="space-y-3">
            {section.items.map((it, i) => (
              <li key={i} className="flex items-start gap-3">
                {it.title && (
                  <div className="mt-1 h-6 w-6 shrink-0 rounded-full bg-neutral-900 text-white dark:bg-white dark:text-black grid place-content-center text-xs font-semibold">
                    {i + 1}
                  </div>
                )}
                <div>
                  {it.title && <div className="font-medium">{it.title}</div>}
                  <ul className="list-disc ml-5 space-y-1 text-sm text-neutral-600 dark:text-neutral-300">
                    {it.desc.map((d, j) => (
                      <li key={j}>{d}</li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}


// function VideoContent({ url }: { url: string }) {
//   return (
//     <div className="space-y-3">
//       <div className="aspect-video w-full overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-800">
//         <iframe
//           className="h-full w-full"
//           src={url}
//           allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
//           allowFullScreen
//           title="Help video"
//         />
//       </div>
//       <p className="text-sm text-neutral-600 dark:text-neutral-300">
//         Replace the URL with your own demo or walkthrough.
//       </p>
//     </div>
//   );
// }

function CreditsPricingContent() {
  const sections = [
    {
      title: "Credits & Pricing",
      desc: [
        "2 free credits to start.",
        "Each request costs 1 credit.",
        "Redesign uses 5 credits.",
        "One-time paid unlock enables Download Code, Editor Mode, and Public Share.",
      ],
    },
  ];
  return (
    <div className="space-y-6">
      {sections.map((section, si) => (
        <div key={si}>
          <h3 className="text-lg font-semibold mb-3">{section.title}</h3>
          <ol className="space-y-3">
            {section.desc.map((d, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="mt-1 h-6 w-6 shrink-0 rounded-full bg-neutral-900 text-white dark:bg-white dark:text-black grid place-content-center text-xs font-semibold">
                  {i + 1}
                </div>
                <div className="text-sm text-neutral-600 dark:text-neutral-300">
                  {d}
                </div>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}
function FAQsContent({ items = DEFAULT_FAQS }: { items?: FAQ[] }) {
  const [openSet, setOpenSet] = useState<Set<number>>(new Set());
  function toggle(i: number) {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }
  return (
     <div className="space-y-8">
      {/* Pricing section */}
    <CreditsPricingContent />
    <div> 
       <h3 className="text-lg font-semibold mb-3">FAQs</h3>
   
    <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
      {items.map((it, i) => {
        const isOpen = openSet.has(i);
        return (
          <div key={i} className="py-3">
            <button
              type="button"
              onClick={() => toggle(i)}
              aria-expanded={isOpen}
              aria-controls={`faq-panel-${i}`}
              className="w-full flex items-center justify-between gap-3 text-left"
            >
              <span className="font-medium">{it.question}</span>
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <div
              id={`faq-panel-${i}`}
              role="region"
              hidden={!isOpen}
              className="mt-2 text-sm text-neutral-700 dark:text-neutral-300"
            >
              {typeof it.answer === 'string' ? <p>{it.answer}</p> : it.answer}
            </div>
          </div>
        );
      })}
    </div>
     </div></div>
  );
}
function MoreContent() {
   const [showFeedback, setShowFeedback] = useState(false);
  return (
    <div className="space-y-4">
    {/* Feedback Section */}
      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 p-4">
        <h3 className="font-medium">Feedback</h3>
        <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-300">
          Have suggestions or found an issue?{" "}
          <button
            onClick={() => setShowFeedback(true)}
            className="underline text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
          >
            Share feedback
          </button>
        </p>
      </div>
      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 p-4">
        <h3 className="font-medium">Contact</h3>
        <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-300">
          Email: <a className="underline" href="mailto:hello@example.com">hello@varnz.ai</a>
        </p>
      </div>

           {showFeedback && (
            <FeedbackForm
              show={showFeedback}
              handleClose={() => setShowFeedback(false)}
            />
          )}
    </div>
  );

}


/* -------------------- Inline Icons (no external deps) -------------------- */

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function HelpCircle({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx("inline-block", className)}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.1 9a3 3 0 1 1 5.8 1c0 2-3 2-3 4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx("inline-block", className)}
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx("inline-block", className)}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx("inline-block", className)}
    >
      <polygon points="6 4 20 12 6 20 6 4" />
    </svg>
  );
}

function MessageIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx("inline-block", className)}
    >
      <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx("inline-block", className)}
    >
      <path d="M5 3v4M3 5h4" />
      <path d="M19 13v4M17 15h4" />
      <path d="M12 7l2 3 3 2-3 2-2 3-2-3-3-2 3-2 2-3z" />
    </svg>
  );
}

function DotIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cx("inline-block", className)}
    >
      <circle cx="12" cy="12" r="4" />
    </svg>
  );
}

function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cx("animate-spin", className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}
function QuestionIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cx("inline-block", className)}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.1 9a3 3 0 1 1 5.8 1c0 2-3 2-3 4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={cx("inline-block", className)}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

